import { Fragment, useCallback, useMemo } from "react";
import { Autocomplete, Divider, TextField } from "@mui/material";
import TaskSelectorDefaultRenderOption from "./components/TaskSelectorDefaultRenderOption/TaskSelectorDefaultRenderOption";
import { ALL_TASKS, SLOT_PROPS } from "./TaskSelector.constants";
import type {
  TaskSelectorOptionOnClickType,
  TaskSelectorProps,
  TaskWithDisabled,
} from "./TaskSelector.types";
import { filterOptions, isOptionEqualToValue } from "./TaskSelector.utils";

const TaskSelector = ({
  task,
  options,
  onChange,
  disabled,
  isLoading,
  autoFocus,
  renderOption = TaskSelectorDefaultRenderOption,
  ...props
}: TaskSelectorProps) => {
  const onLocalChange = useCallback(
    (_e: any, value: any) => {
      onChange?.(value);
    },
    [onChange]
  );

  const slotProps = useMemo(
    () => ({
      ...SLOT_PROPS,
      ...props.slotProps,
    }),
    [props.slotProps]
  );

  const RenderOption = renderOption;

  return (
    <Autocomplete
      loading={isLoading}
      value={task || null}
      openOnFocus
      forcePopupIcon={false} // hide the "arrow" icon
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={isLoading ? "Fetching tasks…" : "Task"}
          {...slotProps?.renderInput?.TextField}
        />
      )}
      onChange={onLocalChange}
      disabled={isLoading || disabled}
      size="small"
      options={options?.length ? options : []}
      // getOptionDisabled={(option) => (option as TaskWithDisabled).disabled ?? false}
      noOptionsText={
        <>
          The selected <em>time-window</em> or <em>PIT</em> might not include any
          tasks
        </>
      }
      includeInputInList={true}
      filterOptions={filterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionKey={(option) => option.task_id ?? option.task_name}
      getOptionLabel={() => task?.task_name ?? ""}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <Fragment key={key}>
            <RenderOption
              option={option as TaskWithDisabled}
              {...optionProps}
              {...slotProps?.renderOption}
              onClick={
                slotProps?.renderOption?.onClick ||
                (optionProps.onClick as TaskSelectorOptionOnClickType)
              }
            />
            {option.task_name === ALL_TASKS && <Divider sx={{ my: 0.5 }} />}
          </Fragment>
        );
      }}
      {...props}
      slotProps={slotProps}
      data-test-id="task-selector"
    />
  );
};

export default TaskSelector;
